import { z } from 'zod'

import { PublicImageSchema } from './public'
import { SHOP_CURRENCY, SHOP_LIMITS, SkuSchema } from './shop'

// Online store, phase 2: shop customers (their own accounts, separate from the CMS users), addresses, a wishlist,
// checkout with demo shipping and demo payment, and orders. The browser only ever sends SKUs, quantities, an address
// and its choices; every price, stock number, shipping price and total is worked out again by the server.

export const SHOP_LOCALES = ['en', 'id', 'zh', 'vi', 'ja'] as const
const ShopLocaleSchema = z.enum(SHOP_LOCALES)

export const CUSTOMER_LIMITS = {
  addresses: 5,
  wishlist: 100,
  passwordMin: 10,
  passwordMax: 200,
  /** Minutes a demo payment waits before it expires and the stock goes back. */
  paymentMinutes: 15,
  /** Orders shown in "My orders". */
  orders: 50,
} as const

// ── Card numbers ─────────────────────────────────────────────────────────
/**
 * True when the text holds a 13–19 digit number (spaces or dashes allowed) that starts like a card (2–6) and passes
 * the Luhn check, i.e. looks like a real card number.
 */
export function looksLikeCardNumber(text: string): boolean {
  return (text.match(/\d[\d -]{11,}\d/g) ?? []).some((run) => {
    const digits = run.replace(/[ -]/g, '')
    return digits.length >= 13 && digits.length <= 19 && /^[2-6]/.test(digits) && luhn(digits)
  })
}

function luhn(digits: string): boolean {
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    let d = Number(digits[digits.length - 1 - i])
    if (i % 2 === 1) d = d * 2 > 9 ? d * 2 - 9 : d * 2
    sum += d
  }
  return sum % 10 === 0
}

/** Free text a visitor types: trimmed, capped, and never a card number (this demo has no card field at all). */
const freeText = (min: number, max: number) =>
  z
    .string()
    .trim()
    .min(min)
    .max(max)
    .refine((s) => !looksLikeCardNumber(s), 'card_number')

// ── Customers ────────────────────────────────────────────────────────────
export const CustomerEmailSchema = z.string().trim().toLowerCase().email().max(200)
export const CustomerPasswordSchema = z.string().min(CUSTOMER_LIMITS.passwordMin).max(CUSTOMER_LIMITS.passwordMax)

export const CustomerSchema = z.object({ id: z.string(), name: z.string(), email: z.string(), createdAt: z.string() })
export type Customer = z.infer<typeof CustomerSchema>

/** GET /api/shop/auth/me: `customer` is null when nobody is signed in. */
export const CustomerMeSchema = z.object({ customer: CustomerSchema.nullable() })

const WishlistKeySchema = z.string().regex(/^[a-z0-9-]{1,120}\/[a-z0-9-]{1,120}$/, 'category/product')

export const SignUpRequestSchema = z.object({
  name: freeText(1, 80),
  email: CustomerEmailSchema,
  password: CustomerPasswordSchema,
  turnstileToken: z.string().max(4096).optional(),
  /** The wishlist kept in this browser before signing up, merged into the account. */
  wishlist: z.array(WishlistKeySchema).max(CUSTOMER_LIMITS.wishlist).optional(),
})
export const SignInRequestSchema = z.object({
  email: CustomerEmailSchema,
  password: z.string().min(1).max(CUSTOMER_LIMITS.passwordMax),
  turnstileToken: z.string().max(4096).optional(),
  wishlist: z.array(WishlistKeySchema).max(CUSTOMER_LIMITS.wishlist).optional(),
})
export const ForgotPasswordRequestSchema = z.object({ email: CustomerEmailSchema })
export const CustomerPasswordChangeSchema = z.object({ currentPassword: z.string().min(1).max(CUSTOMER_LIMITS.passwordMax), newPassword: CustomerPasswordSchema })
export const DeleteAccountRequestSchema = z.object({ password: z.string().min(1).max(CUSTOMER_LIMITS.passwordMax) })

// ── Addresses ────────────────────────────────────────────────────────────
/** Indonesia's 38 provinces. */
export const PROVINCES = [
  'Aceh',
  'Sumatera Utara',
  'Sumatera Barat',
  'Riau',
  'Kepulauan Riau',
  'Jambi',
  'Sumatera Selatan',
  'Kepulauan Bangka Belitung',
  'Bengkulu',
  'Lampung',
  'DKI Jakarta',
  'Jawa Barat',
  'Banten',
  'Jawa Tengah',
  'DI Yogyakarta',
  'Jawa Timur',
  'Bali',
  'Nusa Tenggara Barat',
  'Nusa Tenggara Timur',
  'Kalimantan Barat',
  'Kalimantan Tengah',
  'Kalimantan Selatan',
  'Kalimantan Timur',
  'Kalimantan Utara',
  'Sulawesi Utara',
  'Gorontalo',
  'Sulawesi Tengah',
  'Sulawesi Barat',
  'Sulawesi Selatan',
  'Sulawesi Tenggara',
  'Maluku',
  'Maluku Utara',
  'Papua',
  'Papua Barat',
  'Papua Barat Daya',
  'Papua Tengah',
  'Papua Pegunungan',
  'Papua Selatan',
] as const
export const ProvinceSchema = z.enum(PROVINCES)
export type Province = z.infer<typeof ProvinceSchema>

/** An Indonesian mobile number: 08…, 628… or +628…, 10–15 digits; spaces and dashes are dropped. */
export const PhoneSchema = z
  .string()
  .trim()
  .transform((s) => s.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^(\+62|62|0)8\d{7,12}$/, 'phone'))

export const AddressInputSchema = z.object({
  name: freeText(1, 80),
  phone: PhoneSchema,
  street: freeText(5, 200),
  kelurahan: freeText(0, 60).default(''),
  kecamatan: freeText(0, 60).default(''),
  city: freeText(2, 60),
  province: ProvinceSchema,
  postalCode: z.string().trim().regex(/^\d{5}$/, 'postal_code'),
})
export type AddressInput = z.infer<typeof AddressInputSchema>

export const SavedAddressInputSchema = AddressInputSchema.extend({ isDefault: z.boolean().default(false) })
export const AddressSchema = AddressInputSchema.extend({ id: z.string(), isDefault: z.boolean() })
export type Address = z.infer<typeof AddressSchema>
export const AddressListSchema = z.object({ items: z.array(AddressSchema) })

// ── Wishlist ─────────────────────────────────────────────────────────────
/** A wishlist entry is the product's address in the shop: `<category slug>/<product slug>`. */
export const WishlistSchema = z.object({ items: z.array(z.string()) })
export const WishlistAddRequestSchema = z.object({ items: z.array(WishlistKeySchema).min(1).max(CUSTOMER_LIMITS.wishlist) })

// ── Shipping (demo) ──────────────────────────────────────────────────────
/** Delivery zones from the (made-up) Jakarta warehouse. */
export const SHIPPING_ZONES = ['jabodetabek', 'java', 'sumatra_bali', 'kalimantan_sulawesi', 'east'] as const
export type ShippingZone = (typeof SHIPPING_ZONES)[number]

const ZONE_OF: Record<Province, ShippingZone> = {
  'DKI Jakarta': 'jabodetabek',
  'Jawa Barat': 'jabodetabek',
  Banten: 'jabodetabek',
  'Jawa Tengah': 'java',
  'DI Yogyakarta': 'java',
  'Jawa Timur': 'java',
  Aceh: 'sumatra_bali',
  'Sumatera Utara': 'sumatra_bali',
  'Sumatera Barat': 'sumatra_bali',
  Riau: 'sumatra_bali',
  'Kepulauan Riau': 'sumatra_bali',
  Jambi: 'sumatra_bali',
  'Sumatera Selatan': 'sumatra_bali',
  'Kepulauan Bangka Belitung': 'sumatra_bali',
  Bengkulu: 'sumatra_bali',
  Lampung: 'sumatra_bali',
  Bali: 'sumatra_bali',
  'Nusa Tenggara Barat': 'sumatra_bali',
  'Nusa Tenggara Timur': 'kalimantan_sulawesi',
  'Kalimantan Barat': 'kalimantan_sulawesi',
  'Kalimantan Tengah': 'kalimantan_sulawesi',
  'Kalimantan Selatan': 'kalimantan_sulawesi',
  'Kalimantan Timur': 'kalimantan_sulawesi',
  'Kalimantan Utara': 'kalimantan_sulawesi',
  'Sulawesi Utara': 'kalimantan_sulawesi',
  Gorontalo: 'kalimantan_sulawesi',
  'Sulawesi Tengah': 'kalimantan_sulawesi',
  'Sulawesi Barat': 'kalimantan_sulawesi',
  'Sulawesi Selatan': 'kalimantan_sulawesi',
  'Sulawesi Tenggara': 'kalimantan_sulawesi',
  Maluku: 'east',
  'Maluku Utara': 'east',
  Papua: 'east',
  'Papua Barat': 'east',
  'Papua Barat Daya': 'east',
  'Papua Tengah': 'east',
  'Papua Pegunungan': 'east',
  'Papua Selatan': 'east',
}
export const zoneOf = (province: Province): ShippingZone => ZONE_OF[province]

export const SHIPPING_METHODS = ['jne_reg', 'sicepat_reg', 'pickup'] as const
export const ShippingMethodSchema = z.enum(SHIPPING_METHODS)
export type ShippingMethod = z.infer<typeof ShippingMethodSchema>

/** Fixed demo price tables (whole rupiah per order) and delivery days. `null` = not offered there. */
const SHIPPING_TABLE: Record<ShippingMethod, { label: string; rates: Record<ShippingZone, { price: number; days: [number, number] } | null> }> = {
  jne_reg: {
    label: 'JNE REG',
    rates: {
      jabodetabek: { price: 12_000, days: [1, 2] },
      java: { price: 18_000, days: [2, 3] },
      sumatra_bali: { price: 28_000, days: [3, 5] },
      kalimantan_sulawesi: { price: 38_000, days: [4, 6] },
      east: { price: 65_000, days: [5, 9] },
    },
  },
  sicepat_reg: {
    label: 'SiCepat REG',
    rates: {
      jabodetabek: { price: 11_000, days: [1, 2] },
      java: { price: 16_000, days: [2, 4] },
      sumatra_bali: { price: 26_000, days: [3, 6] },
      kalimantan_sulawesi: { price: 36_000, days: [4, 7] },
      east: { price: 62_000, days: [6, 10] },
    },
  },
  pickup: {
    label: 'Ambil di toko',
    rates: { jabodetabek: { price: 0, days: [0, 1] }, java: null, sumatra_bali: null, kalimantan_sulawesi: null, east: null },
  },
}

export const ShippingOptionSchema = z.object({ method: ShippingMethodSchema, label: z.string(), price: z.number().int(), days: z.tuple([z.number().int(), z.number().int()]) })
export type ShippingOption = z.infer<typeof ShippingOptionSchema>

/** The demo couriers offered for a province, cheapest first after pickup. */
export function shippingOptions(province: Province): ShippingOption[] {
  const zone = zoneOf(province)
  return SHIPPING_METHODS.flatMap((method) => {
    const rate = SHIPPING_TABLE[method].rates[zone]
    return rate ? [{ method, label: SHIPPING_TABLE[method].label, price: rate.price, days: rate.days }] : []
  })
}

// ── Payment ──────────────────────────────────────────────────────────────
/**
 * Who takes the money, per site (CMS → Store → Payments): `demo` (nothing is charged; the demo payment page with its
 * made-up VA numbers and QR) or `xendit` (the visitor pays on Xendit's hosted page). More gateways plug in here.
 */
export const PAYMENT_PROVIDERS = ['demo', 'xendit'] as const
export const PaymentProviderSchema = z.enum(PAYMENT_PROVIDERS)
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>

/** The demo methods the visitor picks at checkout. Labels only: nothing is sent to any payment provider. */
export const DEMO_PAYMENT_METHODS = ['qris', 'va_bca', 'va_mandiri', 'va_bri', 'gopay', 'ovo', 'dana'] as const
/** Every stored method: the demo ones, and `xendit` (the visitor chooses QRIS, VA or e-wallet on Xendit's page). */
export const PAYMENT_METHODS = [...DEMO_PAYMENT_METHODS, 'xendit'] as const
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS)
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>
export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  qris: 'QRIS',
  va_bca: 'BCA Virtual Account',
  va_mandiri: 'Mandiri Virtual Account',
  va_bri: 'BRI Virtual Account',
  gopay: 'GoPay',
  ovo: 'OVO',
  dana: 'DANA',
  xendit: 'Xendit',
}
export const paymentKind = (m: PaymentMethod): 'qris' | 'va' | 'ewallet' | 'gateway' => (m === 'xendit' ? 'gateway' : m === 'qris' ? 'qris' : m.startsWith('va_') ? 'va' : 'ewallet')

/** A Xendit channel code as a label people know: BCA_VIRTUAL_ACCOUNT → BCA Virtual Account, QRIS → QRIS. */
export function channelLabel(code: string | null): string | null {
  if (!code) return null
  const known: Record<string, string> = { QRIS: 'QRIS', OVO: 'OVO', DANA: 'DANA', GOPAY: 'GoPay', SHOPEEPAY: 'ShopeePay', LINKAJA: 'LinkAja', ASTRAPAY: 'AstraPay', CARDS: 'Card', JENIUSPAY: 'Jenius Pay' }
  if (known[code]) return known[code]
  const va = /^([A-Z0-9]+)_VIRTUAL_ACCOUNT$/.exec(code)
  if (va) return `${va[1] === 'MANDIRI' ? 'Mandiri' : va[1]} Virtual Account`
  return code.replace(/_/g, ' ')
}

// ── Orders ───────────────────────────────────────────────────────────────
export const ORDER_STATUSES = ['pending_payment', 'paid', 'packed', 'shipped', 'delivered', 'cancelled', 'expired', 'refunded'] as const
export const OrderStatusSchema = z.enum(ORDER_STATUSES)
export type OrderStatus = z.infer<typeof OrderStatusSchema>

/** The statuses a CMS user can set, from which statuses. Cancelling before shipping puts the stock back; a refund is a mark only. */
export const ADMIN_TRANSITIONS: Partial<Record<OrderStatus, readonly OrderStatus[]>> = {
  packed: ['paid'],
  shipped: ['paid', 'packed'],
  delivered: ['shipped'],
  cancelled: ['pending_payment', 'paid', 'packed'],
  refunded: ['paid', 'packed', 'shipped', 'delivered', 'cancelled'],
}
export const ADMIN_STATUSES = Object.keys(ADMIN_TRANSITIONS) as OrderStatus[]

/** Order numbers: `<prefix>-<YYMM>-<6 digits>`, e.g. KB-2610-000123. */
export const OrderNumberSchema = z.string().trim().toUpperCase().regex(/^[A-Z]{1,6}-\d{4}-\d{6,}$/, 'order_number')

export const CheckoutRequestSchema = z
  .object({
    items: z
      .array(z.object({ sku: SkuSchema, qty: z.number().int().min(1).max(SHOP_LIMITS.qtyPerLine) }))
      .min(1)
      .max(SHOP_LIMITS.cartLines),
    locale: ShopLocaleSchema,
    /** Guests give an email; a signed-in customer's own email is used. */
    email: CustomerEmailSchema.optional(),
    /** A saved address (signed in) or one typed in. */
    addressId: z.string().max(64).optional(),
    address: AddressInputSchema.optional(),
    saveAddress: z.boolean().default(false),
    shipping: ShippingMethodSchema,
    /** A demo method; ignored when the site takes payment through a gateway (the visitor chooses on its page). */
    payment: PaymentMethodSchema,
    /** A discount code; the server checks it again and works out the discount itself. */
    code: z.string().trim().max(40).optional(),
    /** The total the visitor saw; a different server total is refused so nobody pays an amount they did not see. */
    expectedTotal: z.number().int().min(0),
  })
  .refine((r) => !!r.addressId !== !!r.address, { message: 'address_or_id', path: ['address'] })
export type CheckoutRequest = z.infer<typeof CheckoutRequestSchema>

export const ShippingOptionsRequestSchema = z.object({ province: ProvinceSchema })

export const OrderItemSchema = z.object({
  sku: z.string(),
  title: z.string(),
  color: z.string(),
  colorHex: z.string(),
  size: z.string(),
  qty: z.number().int(),
  unitPrice: z.number().int(),
  lineTotal: z.number().int(),
  /** Link back to the product (null once it is gone from the shop). */
  path: z.string().nullable(),
  /** The product photo for the line's colour, looked up when the order is read (orders do not store photos). */
  image: PublicImageSchema.nullable(),
})
export type OrderItem = z.infer<typeof OrderItemSchema>

export const OrderEventSchema = z.object({ status: OrderStatusSchema, at: z.string(), note: z.string() })
export type OrderEvent = z.infer<typeof OrderEventSchema>

/** An address as stored on an order. Tracking by order number shows only the name's first letter, the city and the province. */
export const OrderAddressSchema = z.object({
  name: z.string(),
  phone: z.string(),
  street: z.string(),
  kelurahan: z.string(),
  kecamatan: z.string(),
  city: z.string(),
  province: z.string(),
  postalCode: z.string(),
})

export const OrderViewSchema = z.object({
  number: z.string(),
  status: OrderStatusSchema,
  createdAt: z.string(),
  locale: ShopLocaleSchema,
  currency: z.literal(SHOP_CURRENCY),
  email: z.string(),
  address: OrderAddressSchema,
  shipping: z.object({ method: ShippingMethodSchema, label: z.string(), price: z.number().int(), days: z.tuple([z.number().int(), z.number().int()]) }),
  payment: z.object({
    method: PaymentMethodSchema,
    label: z.string(),
    /** When an unpaid order expires and its stock goes back. */
    expiresAt: z.string(),
    /** A made-up virtual account number (VA methods only). */
    vaNumber: z.string().nullable(),
    paidAt: z.string().nullable(),
    /** `demo`, or the gateway the visitor pays through. */
    provider: PaymentProviderSchema.default('demo'),
    /** The gateway's hosted payment page, while the order waits for payment (never in the masked tracking view). */
    payUrl: z.string().nullable().default(null),
    /** How it was paid on the gateway's page, e.g. "QRIS" or "BCA Virtual Account". */
    channel: z.string().nullable().default(null),
  }),
  items: z.array(OrderItemSchema),
  subtotal: z.number().int(),
  /** Rupiah off from a discount code (goods and/or shipping); total = subtotal + shipping − discount. */
  discount: z.number().int().default(0),
  promoCode: z.string().nullable().default(null),
  total: z.number().int(),
  trackingNumber: z.string().nullable(),
  events: z.array(OrderEventSchema),
  /** Whether a signed-in customer owns it (else a guest order). */
  guest: z.boolean(),
})
export type OrderView = z.infer<typeof OrderViewSchema>

/** Answer to "Place order": the order and the key that opens its page in this browser (guests have no account). */
export const CheckoutResultSchema = z.object({ number: z.string(), key: z.string(), order: OrderViewSchema })
export type CheckoutResult = z.infer<typeof CheckoutResultSchema>

export const OrderSummarySchema = z.object({ number: z.string(), status: OrderStatusSchema, createdAt: z.string(), total: z.number().int(), itemCount: z.number().int() })
export const OrderSummaryListSchema = z.object({ items: z.array(OrderSummarySchema) })

export const OrderAccessQuerySchema = z.object({ key: z.string().regex(/^[a-f0-9]{64}$/).optional() })
export const SimulatePaymentRequestSchema = z.object({ action: z.enum(['paid', 'expired']), key: z.string().regex(/^[a-f0-9]{64}$/).optional() })
export const TrackOrderRequestSchema = z.object({ number: OrderNumberSchema, email: CustomerEmailSchema })

// ── Admin ────────────────────────────────────────────────────────────────
export const AdminOrderListQuerySchema = z.object({
  status: OrderStatusSchema.optional(),
  /** Inclusive dates, YYYY-MM-DD (Jakarta time). */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
})

export const AdminOrderRowSchema = z.object({
  id: z.string(),
  number: z.string(),
  status: OrderStatusSchema,
  name: z.string(),
  email: z.string(),
  total: z.number().int(),
  itemCount: z.number().int(),
  payment: PaymentMethodSchema,
  shipping: ShippingMethodSchema,
  createdAt: z.string(),
  guest: z.boolean(),
})
export type AdminOrderRow = z.infer<typeof AdminOrderRowSchema>
export const AdminOrderListSchema = z.object({ items: z.array(AdminOrderRowSchema), total: z.number().int(), limit: z.number().int(), offset: z.number().int() })

export const AdminOrderDetailSchema = OrderViewSchema.extend({
  id: z.string(),
  customerId: z.string().nullable(),
  anonymised: z.boolean(),
  /** The gateway's references, for looking the payment up in its dashboard (null for demo orders). */
  gateway: z.object({ provider: PaymentProviderSchema, sessionId: z.string().nullable(), paymentId: z.string().nullable(), channel: z.string().nullable() }).nullable().default(null),
})
export type AdminOrderDetail = z.infer<typeof AdminOrderDetailSchema>

export const AdminOrderStatusRequestSchema = z
  .object({
    status: z.enum(['packed', 'shipped', 'delivered', 'cancelled', 'refunded']),
    trackingNumber: z.string().trim().regex(/^[A-Za-z0-9-]{6,40}$/, 'tracking_number').optional(),
    note: z.string().trim().max(300).default(''),
  })
  .refine((r) => r.status !== 'shipped' || !!r.trackingNumber, { message: 'tracking_required', path: ['trackingNumber'] })

export const AdminCustomerRowSchema = z.object({ id: z.string(), name: z.string(), email: z.string(), orders: z.number().int(), createdAt: z.string(), lastLoginAt: z.string().nullable() })
export type AdminCustomerRow = z.infer<typeof AdminCustomerRowSchema>
export const AdminCustomerListSchema = z.object({ items: z.array(AdminCustomerRowSchema), total: z.number().int(), limit: z.number().int(), offset: z.number().int() })
export const AdminCustomerListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
})

// ── Sales demo reset (phase 4) ───────────────────────────────────────────
/** GET /api/admin/shop/demo-reset: whether this shop is a demo wiped every night (SHOP_DEMO_RESET), and when it last was. */
export const DemoResetStateSchema = z.object({ enabled: z.boolean(), lastResetAt: z.string().nullable() })
/** POST /api/admin/shop/demo-reset: what "Reset demo now" deleted. */
export const DemoResetResultSchema = z.object({ orders: z.number(), customers: z.number(), prices: z.number(), lastResetAt: z.string() })
